import { z } from 'zod';
import { IsoDatetime } from './primitives.js';
import { TrustLevel } from './trust.js';

const Id = z.string().max(40);

// Protocol = Trigger → Condition → Action (01 §3, 02 §4). T and C are
// deterministic and simulatable; A is either a deterministic primitive or
// open_situation, which hands the goal to the thinking core (02 §4.1).

export const ProtocolTrigger = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('cron'),
    expr: z.string().min(1),
    timezone: z.string().optional(),
  }),
  z.object({
    type: z.literal('event'),
    source: z.enum(['memory_fact', 'calendar', 'institution_message', 'ledger', 'sensor_report']),
    match: z.record(z.any()).default({}),
  }),
  z.object({
    // Inventory threshold (S11) — an entity state crossing a bound.
    type: z.literal('threshold'),
    entity_id: Id.optional(),
    expression: z.string().min(1),
    // S63: how long before the event the protocol wakes.
    lead_time_minutes: z.number().int().min(0).optional(),
  }),
  z.object({
    type: z.literal('message_intent'),
    intent: z.string().min(1),
  }),
  z.object({
    type: z.literal('state'),
    watch: z.string().min(1),
    equals: z.string().min(1),
  }),
]);
export type ProtocolTrigger = z.infer<typeof ProtocolTrigger>;

// Condition — all/any expressions over valid facts and state; evaluated
// against the same hydrate_state the graph uses (one truth).
export const ProtocolCondition = z.object({
  all: z.array(z.string().min(1)).optional(),
  any: z.array(z.string().min(1)).optional(),
  timeout_minutes: z.number().int().positive().optional(),
});
export type ProtocolCondition = z.infer<typeof ProtocolCondition>;

export const ProtocolAction = z.discriminatedUnion('type', [
  z.object({
    // The recommended default: hand the goal to the thinking core.
    type: z.literal('open_situation'),
    goal: z.string().min(1),
    // Memory keys / entity ids to inject as context.
    context: z.array(z.string()).default([]),
    blueprint_ref: z.string().optional(),
  }),
  z.object({
    type: z.literal('notify'),
    message: z.string().min(1),
    target: z.string().optional(),
  }),
  z.object({
    // Actuation through the channel ladder (01 §8).
    type: z.literal('actuate'),
    action: z.record(z.any()),
  }),
  z.object({
    type: z.literal('ledger_entry'),
    account_id: Id,
    amount_minor: z.number().int(),
    note: z.string().optional(),
  }),
  z.object({
    type: z.literal('set_fact'),
    predicate: z.string().min(1),
    object_text: z.string().optional(),
  }),
  z.object({
    type: z.literal('emit_obligation'),
    template: z.string().min(1),
    args: z.record(z.any()).default({}),
  }),
  z.object({
    type: z.literal('release_lock'),
    commitment_id: Id,
  }),
]);
export type ProtocolAction = z.infer<typeof ProtocolAction>;

export const Protocol = z.object({
  id: Id,
  household_id: Id,
  home_entity_id: Id.nullable(),
  name: z.string().min(1),
  enabled: z.boolean().default(true),
  trigger: ProtocolTrigger,
  condition: ProtocolCondition.default({}),
  action: ProtocolAction,
  // Maximum trust level this protocol may act at — child protocols are
  // always 3-4 (D-S1).
  max_trust_level: TrustLevel.default(1),
  blueprint_id: Id.nullable(),
  blueprint_version: z.number().int().positive().nullable(),
  created_at: IsoDatetime,
  updated_at: IsoDatetime,
  deleted_at: IsoDatetime.nullable(),
});
export type Protocol = z.infer<typeof Protocol>;

export const BlueprintRunMode = z.enum(['single', 'restart', 'queued']);
export type BlueprintRunMode = z.infer<typeof BlueprintRunMode>;

// A parametric T→C→A skeleton plus the schema of the parameters a
// household fills (01 §3.2, 02 §4.2).
export const BlueprintTemplate = z.object({
  trigger: z.array(ProtocolTrigger).min(1),
  condition: ProtocolCondition.default({}),
  action: z.array(ProtocolAction).min(1),
  run_mode: BlueprintRunMode.default('single'),
  // Autonomy ceiling for instances — a hard bound even against free
  // judgment (decision 12).
  trust_ceiling: TrustLevel,
  // Per-instance money cap (S16); NULL = household ladder.
  budget_cap_minor: z.number().int().nullable(),
});
export type BlueprintTemplate = z.infer<typeof BlueprintTemplate>;

export const Blueprint = z.object({
  // Global library — no household_id.
  id: Id,
  slug: z.string().min(1).max(80),
  version: z.number().int().positive().default(1),
  // Thematic onboarding pack: 'pets', 'live-in-staff', 'toddlers', ...
  pack: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable(),
  template: BlueprintTemplate,
  // JSON Schema of the parameters (stored shape; validated on install).
  param_schema: z.record(z.any()),
  max_trust_level: TrustLevel.default(1),
  author_kind: z.enum(['famanage', 'community']).default('famanage'),
  created_at: IsoDatetime,
  updated_at: IsoDatetime,
});
export type Blueprint = z.infer<typeof Blueprint>;

// A household's parameter values on a blueprint (01 §3.3) — "the driver's
// lead time is 10 minutes" (S63). NULL entity = household default.
export const BlueprintParams = z.object({
  id: Id,
  household_id: Id,
  blueprint_id: Id,
  entity_id: Id.nullable(),
  values: z.record(z.any()).default({}),
  set_by_user_id: Id.nullable(),
  created_at: IsoDatetime,
  updated_at: IsoDatetime,
});
export type BlueprintParams = z.infer<typeof BlueprintParams>;

export const ProtocolRunOutcome = z.enum([
  'fired',
  'condition_false',
  'action_taken',
  'situation_opened',
  'failed',
]);
export type ProtocolRunOutcome = z.infer<typeof ProtocolRunOutcome>;

// A protocol tick: the condition check and the situation it opened (01 §3.4).
// Append-only.
export const ProtocolRun = z.object({
  id: Id,
  protocol_id: Id,
  household_id: Id,
  outcome: ProtocolRunOutcome,
  situation_id: Id.nullable(),
  detail: z.record(z.any()).default({}),
  created_at: IsoDatetime,
});
export type ProtocolRun = z.infer<typeof ProtocolRun>;
